import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useControl,
  useResultFormContext,
} from '../../components/ControlLayout/ControlLayout';

import { Controller } from 'react-hook-form';
import { Rules } from './components/Rules';
import { useGetControlFiles } from '../../../../traits/api/controls/useGetControlFiles';
import { Photos } from './components/Photos';
import { Textarea } from './components/Textarea';
import { AccordionHeader } from './components/AccordionHeader';

import { S } from './ResultAndActions.styles';
import { Fines } from './components/Fines';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { RadioInput } from '../../../../components/RadioInput';
import { useGetReportTextTemplate } from '../../../../traits/api/reportTextTemplates/useGetReportTextTemplate';
import { useGetReportTextTemplates } from '../../../../traits/api/reportTextTemplates/useGetReportTextTemplates';
import { useGetStatistics } from '../../../../traits/api/controls/useGetStatistics';
import { useAppConfigContext } from '../../../../config/hooks/useAppConfig';
import { normalizeForQuill } from '../../utils/normalizeForQuill';
import { ReportTextFieldId } from '../../utils/reportTextGeneration';
import { GenerateReportTextDialog } from './components/GenerateReportTextDialog';

type ResultAndActionsLocationState = {
  openGenerateReportText?: boolean;
};

export const ResultAndActions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useControl();
  const { config } = useAppConfigContext();

  const { trigger, watch, setValue, control, getValues } =
    useResultFormContext();
  const summaryTemplate = useGetReportTextTemplate('summaryAndRecommendations');
  const reportTextTemplates = useGetReportTextTemplates();
  const statistics = useGetStatistics(data.inspectionId, data.controlId);

  const [klausValue, setKlausValue] = React.useState<string>('');
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = React.useState(false);
  const [editorGeneration, setEditorGeneration] = React.useState(0);

  const isPersondataVerified = Boolean(data.personDataVerifiedAt);
  const isGenerateFeatureEnabled =
    config.ENABLE_PERSONDATA_REPORT_TEXT_GENERATION;
  const canOpenGenerateDialog =
    isGenerateFeatureEnabled &&
    isPersondataVerified &&
    Boolean(reportTextTemplates.data) &&
    Boolean(statistics.data);
  const shouldOpenGenerateFromPersondata = Boolean(
    (location.state as ResultAndActionsLocationState | null)
      ?.openGenerateReportText
  );

  useEffect(() => {
    setKlausValue(getValues('typeOfClause') ?? '');
  }, [watch('typeOfClause')]);

  useEffect(() => {
    if (!shouldOpenGenerateFromPersondata) return;

    if (!isGenerateFeatureEnabled || !isPersondataVerified) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (!canOpenGenerateDialog) return;

    setIsGenerateDialogOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    shouldOpenGenerateFromPersondata,
    isGenerateFeatureEnabled,
    isPersondataVerified,
    canOpenGenerateDialog,
    navigate,
    location.pathname,
  ]);

  // AF21, AF12
  // Standard, Value, Own

  const klausValueDropdown = [
    {
      label: 'Standardklausuler',
      value: 'Standard',
    },
    {
      label: 'Standardklausuler med eget värde',
      value: 'StandardWithValue',
    },
    {
      label: 'Egna klausuler',
      value: 'Own',
    },
  ];

  const klausTypeDropdown = [
    {
      label: 'AF21',
      value: 'AF21',
    },
    {
      label: 'AF12',
      value: 'AF12',
    },
  ];

  const files = useGetControlFiles(data.inspectionId, data.controlId);

  const shellProtectionFile = files.data
    ?.filter((file) => file.type === 'ShellProtection')
    .filter((file) => !watch('deletedFiles').includes(file.controlFileId));

  const workEnvironmentFile = files.data
    ?.filter((file) => file.type === 'WorkEnvironment')
    .filter((file) => !watch('deletedFiles').includes(file.controlFileId));
  const { state } = useParams();
  const navigateToDeviation = async () => {
    const isValid = await trigger();
    if (!isValid) return;
    navigate(`/kontroller/${state}/${data.inspectionId}/avvikelser`);
  };

  const navigateToSummary = async () => {
    navigate(`/kontroller/${state}/${data.inspectionId}/bedomning`);
  };

  React.useEffect(() => {
    const onScroll = () => {
      localStorage.setItem('RAA', window.scrollY + '');
    };

    let scroll = Number(localStorage.getItem('RAA'));
    if (!scroll) {
      scroll = 0;
    }
    new Promise((r) => setTimeout(r, 200)).then(() => {
      window.scrollTo({
        top: scroll,
        left: 0,
      });
    });

    window.removeEventListener('s croll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const generateConclution = () => {
    if (!summaryTemplate.data?.body) return;
    setValue(
      'summaryAndRecommendations',
      normalizeForQuill(summaryTemplate.data.body)
    );
  };

  const applyGeneratedReportText = (
    writes: Partial<Record<ReportTextFieldId, string>>
  ) => {
    for (const [fieldId, html] of Object.entries(writes)) {
      if (html === undefined) continue;
      setValue(fieldId as ReportTextFieldId, normalizeForQuill(html), {
        shouldDirty: true,
      });
    }

    // Quill applies a new `value` reliably only while mounting, so remount the
    // fields to make the written text visible instead of only saved.
    setEditorGeneration((generation) => generation + 1);
  };

  return (
    <S.ResultContainer>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <S.SortButton aria-label="Customise options">
            <S.H6 color="secondary">Typ av klausul</S.H6>
          </S.SortButton>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <Controller
            name={'typeOfClause'}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <S.DropDown sideOffset={5}>
                {klausValueDropdown.map((item) => (
                  <S.DropDownItem
                    key={item.value}
                    onClick={() => {
                      field.onChange(item.value);
                    }}
                  >
                    {field.value === item.value && (
                      <S.Icon
                        src="CheckCircle"
                        width={20}
                        height={20}
                        color="secondary"
                      />
                    )}

                    <S.DropDownItemText color="secondary">
                      {item.label}
                    </S.DropDownItemText>
                  </S.DropDownItem>
                ))}
              </S.DropDown>
            )}
          />
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <S.SortButton aria-label="Customise options">
            <S.H6 color="secondary">Typ av regelverk</S.H6>
          </S.SortButton>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <Controller
            name={'regulationType'}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <S.DropDown sideOffset={5}>
                {klausTypeDropdown.map((item) => (
                  <S.DropDownItem
                    key={item.value}
                    onClick={() => {
                      field.onChange(item.value);
                    }}
                  >
                    {field.value === item.value && (
                      <S.Icon
                        src="CheckCircle"
                        width={20}
                        height={20}
                        color="secondary"
                      />
                    )}

                    <S.DropDownItemText color="secondary">
                      {item.label}
                    </S.DropDownItemText>
                  </S.DropDownItem>
                ))}
              </S.DropDown>
            )}
          />
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <S.ResultInner
        key={editorGeneration}
        type="multiple"
        paddingbottom={72}
        defaultValue={['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11']}
      >
        <S.TitleRow>
          <S.H4>Resultat/åtgärder</S.H4>
          {isGenerateFeatureEnabled && (
            <S.Button
              $outlined
              $width={220}
              onClick={() => setIsGenerateDialogOpen(true)}
              disabled={!canOpenGenerateDialog}
              title={
                !isPersondataVerified
                  ? 'Markera Persondata som verifierad först'
                  : undefined
              }
            >
              Generera rapporttext
            </S.Button>
          )}
        </S.TitleRow>
        <S.Body value="1">
          <S.Header>
            <S.Icon src="Edit" />
            <S.H6>Sammanfattning av resultat</S.H6>
          </S.Header>

          <S.BodyInner>
            <Textarea
              placeholder="Sammanfatta och skriv eventuella rekommendationer här..."
              name="summaryAndRecommendations"
              label="Sammanfattning"
            />
            <S.Button
              $outlined
              onClick={generateConclution}
              disabled={!summaryTemplate.data?.body}
            >
              Generera sammanfattning
            </S.Button>
          </S.BodyInner>
        </S.Body>
        <S.Body value="3">
          <AccordionHeader title="Elektronisk personalliggare (EPL) – Skatteförfarandelagen" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="workRegisterResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('workRegisterActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="workRegisterActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="workRegisterActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules rule="Personalliggare på en byggarbetsplats skall föras elektroniskt. Detta enligt 39 kap. 11a § Skatteförfarandelagen. Byggherre är i huvudregeln ansvarig att tillhandahålla utrustning för personalliggare, dock finns det vissa undantag från denna skyldighet. Den som bedriver byggverksamhet på en byggarbetsplats där byggherren tillhandahållit utrustning ska föra en elektronisk personalliggare. Regler om personalliggarens form, dess innehåll och hur den ska föras framgår av Skatteverkets föreskriver om personalliggare (SKVFS 2015:6). Av Skatteförfarandelagen framgår även att personalliggaren skall hållas tillgänglig för Skatteverket och byggherren på byggarbetsplatsen (39kap. 12 § andra stycket Skatteförfarandelagen). Om entreprenören inte fullgör sina skyldigheter att föra personalliggare finns det lagstadgade föreläggande med vite som sanktioner (44 kap. 1§ Skatteförfarandelagen). " />
            <Rules
              title="Definition viten EPL"
              rule="Vid konstaterade brister i den elektroniska personalliggare ska entreprenören betala vite om 12 500 kr per tillfälle och 2 500 kr för varje person som vid en kontroll vistas på arbetsplatsen men inte är dokumenterad i den elektroniska personalliggaren."
            />
            <Fines
              inputLabel="Antal personer som ej är på plats"
              klausValue={klausValue}
              nameFine="eplFine"
              nameValue="eplNumber"
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="5">
          <AccordionHeader title="Brott mot ID06 regelverk" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="id06DeviationsResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('id06DeviationsActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="id06DeviationsActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="id06DeviationsActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules
              rule="ID06 används för närvaroregistrering av personer i arbetsplatsens personalliggare, digitalisering av arbetares utbildningsbevis och inpasserings- och låsfunktioner i lokala system. I enlighet med ID06 Allmänna bestämmelser ska användarföretag se till att anställda innehar giltigt ID06-kort vilket ska bäras synligt. Vidare ska anställda på anmodan kunna uppvisa giltig legitimation (pass eller legitimationshandling med fotografi utfärdat av en myndighet).  Ifall det upptäcks felaktigheter eller brister som påverkar hantering av ID06-system skall detta utan dröjsmål underrättas för en ansvarige. Om på arbetsplatsen upptäcks personer som inte uppfyller krav i enlighet med de allmänna bestämmelserna för ID06 kan dessa personer avvisas från arbetsplatsen.
Användarföretag har rätt att beställa och registrera ID06-kort till anställda, för vilka användarföretaget betalar lön och lagstadgade arbetsgivaravgifter (i EU-direktiv benämnt socialavgifter)."
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="6">
          <AccordionHeader title="Avsaknad av giltig legitimation" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="identificationResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('identificationActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="identificationActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="identificationActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules rule="I enlighet med ID06 Allmänna bestämmelser ska användarföretag se till att anställda innehar giltigt ID06-kort vilket ska bäras synligt. Vidare ska anställda på anmodan kunna uppvisa giltig legitimation (pass eller legitimationshandling med fotografi utfärdat av en myndighet).  Ifall det upptäcks felaktigheter eller brister som påverkar hantering av ID06-system skall detta utan dröjsmål underrättas för en ansvarige. Om på arbetsplatsen upptäcks personer som inte uppfyller krav i enlighet med de allmänna bestämmelserna för ID06 kan dessa personer avvisas från arbetsplatsen." />
            <Rules
              title="Definition viten giltig id-handling samt id06-kort"
              rule="För varje person (oavsett om arbetsgivaren är Entreprenören eller underentreprenören i något led) som vid en kontroll saknar giltigt id-handling, giltigt ID06-kort ska Entreprenören betala vite om 10 000 kr per ett saknat dokument."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="idFine"
              nameValue="idNumber"
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="7">
          <AccordionHeader title="Kontroll av arbetstillstånd och uppehållshandlingar" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="workPermitResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('workPermitActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="workPermitActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="workPermitActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules rule="Arbetstillstånd är ett tillstånd att arbeta i Sverige. En utlänning som skall arbeta i Sverige på grund av anställning här eller utomlands skall ha arbetstillstånd. Arbetstillstånd får ges till en utlänning utanför EU som har erbjudits en anställning enligt 6 kap. 2 § Utlänningslagen. Arbetstillstånd skall alltid knytas till en viss arbetsgivare eller avse ett visst slag av arbete. Det är arbetsgivarens ansvar att kontrollera att personen som anlitas från ett land utanför EU har rätt att vara och arbeta i Sverige. Det är även arbetsgivarens skyldighet att underrätta Skatteverket om anställningen. Den som medvetet eller av oaktsamhet anställer person som inte har rätt att vara eller arbeta i Sverige kan få betala straffavgifter (20 kap. 12 § Utlänningslag (2005:716)), fråntas rätten till offentliga stöd och förmåner under fem år, bli återbetalningsskyldig till tidigare utbetalda bidrag och/eller dömas till böter eller fängelse enligt 20 kap. 5 § Utlänningslagen (2005:716).  Undantag för specialistkunskap: Av 5 kap. 2 § p 10 utlänningsförordningen framgår att en utlänning som har specialistuppgifter inom en internationell koncern och som i denna egenskap tillfälligt arbetar i Sverige är undantagen kravet på arbetstillstånd för en sammanlagd tid som understiger ett år. Detta undantag kan användas enbart en gång, det vill säga som en längre period som är upp till ett år eller i ett antal kortare perioder som sammanlagt understiger ett år. Har personen arbetat i Sverige i en sammanlagd tid som understiger ett år är det kravet på arbetstillstånd som aktualiseras därefter. Undantaget förnyas inte, såsom är fallet i ex de undantag som gäller ett antal månader från inresan eller inom ett år. När det gäller begreppet specialist kan en viss vägledning tas från ICT-lagstiftningen (6 b kap. utlänningslagen). Personen som åberopar undantaget för specialister ska kunna uppvisa att han eller hon har de yrkeskvalifikationer och den erfarenhet eller utbildning som krävs för uppdraget som specialist. Man kan säga att personen som åberopar undantaget bör kunna visa unik kompetens och erfarenhet, att personen inte lätt går att ersätta, personen har en förhållandevis hög position inom koncernen. Även lönens och andra ersättningars storlek bör beaktas." />
            <Rules
              title="Definition viten ej styrkt arbetstillstånd"
              rule="För varje person (oavsett om arbetsgivaren är Entreprenören eller underentreprenören i något led) som vid en kontroll inte kan styrka arbetstillstånd ska Entreprenören betala vite om 100 000 kr."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="workPermitFine"
              nameValue="workPermitNumber"
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="8">
          <AccordionHeader title="Utstationeringsregelverket" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="secondmentRegulationsResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('secondmentRegulationsActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="secondmentRegulationsActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="secondmentRegulationsActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules
              rule="Utstationerade arbetstagare garanteras ett visst skydd enligt bestämmelser i EU:s utstationeringsdirektiv (Europaparlamentets och rådets direktiv 96/71/EG av den 16
              december 1996 om utstationering av arbetstagare i samband med tillhandahållande av tjänster).
              Utstationeringsregler innebär att utländska arbetsgivare som utstationerar arbetstagare till
              Sverige ska anmäla arbetstagarna och en kontaktperson (29 § lagen (1999:678) om utstationering av arbetstagare). Om arbetsgivare brister i sina skyldigheter och inte anmäler en utstationering bryter mot regelverket. I konsekvenser kan detta leda till att arbetsgivare få betala sanktionsavgift som är 20 000 kronor (12 § Förordning (2017:319) om utstationering av arbetstagare). Detsamma gäller om kontaktperson inte registreras eller om dokumentation om anmälan inte lämnas till tjänstemottagaren i tid.
              Om tjänstemottagaren inte får dokumentation om anmälan ska tjänstemottagaren underrätta Arbetsmiljöverket senast tre dagar efter det att arbetet har påbörjats (31 § lagen (1999:678) om utstationering av arbetstagare). Ifall tjänstemottagare har överträtt bestämmelsen om underrättelseskyldighet ska en sanktionsavgift tas ut enligt 37 § andra stycket lagen (1999:678) om utstationering av arbetstagare."
            />

            <Rules
              title="Definition viten kan ej uppvisa A1-intyg"
              rule="För varje utstationerad person som inte kan uppvisa ett relevant A1-intyg ska Entreprenören betala vite om 25 000 kr per tillfälle."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="a1Fine"
              nameValue="a1Number"
            />

            <Rules
              title="Definition viten överträdelse av anmälningsskyldighet till Arbetsmiljöverkets utstationeringsregister"
              rule="Vid överträdelse av anmälningsskyldighet till Arbetsmiljöverket ska Entreprenören betala vite om 50 000 kr per tillfälle. Ett särskilt vitesbelopp gäller vid överträdelser i utstationeringslagen och utstationeringsförordningen. Huvudregeln är att varje utstationerad person utgör en utstationering som måste anmälas. Viten uppgår därför till 20 000 kr per överträdelse som består av en utstationering."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="utstationeringsFine"
              nameValue="utstationeringsNumber"
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="4">
          <AccordionHeader title="Avstämning mot förgodkända UE" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="preApprovedSubcontractorResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('preApprovedSubcontractorActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="preApprovedSubcontractorActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="preApprovedSubcontractorActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules rule="I enlighet med avtalsvillkor för underentreprenörer och bemanningsföretag vid anlitade på Byggavtalets tillämpningsområde finns det särskilda entreprenadsrättsliga villkor. Ett av kraven är att underentreprenören alt. bemanningsföretaget skriftligen rapporterar vilka underentreprenörer och/eller bemanningsföretag denne anlitar i alla led. " />
            <Rules
              title="Definition viten förgodkänd UE"
              rule="För varje underentreprenör i något led som inte är förgodkänd ska Entreprenören betala vite om 30 000 kr."
            />
            <Fines
              inputLabel="Antal ej förgodkända underentreprenörer"
              klausValue={klausValue}
              nameFine="acceptedSubcontractorFine"
              nameValue="acceptedSubcontractorNumber"
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="2">
          <AccordionHeader title="Arbetsmiljö" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="workEnvironmentResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('workEnvironmentActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="workEnvironmentActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="workEnvironmentActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            {workEnvironmentFile && workEnvironmentFile.length > 0 && (
              <Photos data={workEnvironmentFile} />
            )}

            <Rules rule="För alla arbetsgivare gäller föreskrifterna om systematiskt arbetsmiljöarbete (AFS 2011:1). Krav på systematiskt arbetsmiljöarbete innebär att undersöka, genomföra och följa upp verksamhet för att förebygga olyckor och ohälsa. Det skall finnas arbetsmiljöpolicy och rutiner som dokumenteras skriftligt om det finns minst 10 arbetstagare i verksamheten (5 §). Syftet med kraven i arbetsmiljölagen, arbetsmiljöförordningen och Arbetarskyddsstyrelsens och Arbetsmiljöverkets regler är att förebygga ohälsa och olycksfall i arbetet och att uppnå en tillfredsställande arbetsmiljö." />
            <Rules
              title="Definition viten arbetsmiljö"
              rule="Vid konstaterade brister i arbetsmiljö ska Entreprenören betala vite om 10 000 kr per tillfälle."
            />
            <Fines
              inputLabel="Antal brister"
              klausValue={klausValue}
              nameFine="workingEnvironmentFine"
              nameValue="workingEnvironmentNumber"
            />
            <Rules
              title="Definition viten arbete med ställningar"
              rule="För varje person (oavsett om arbetsgivaren är Entreprenören eller underentreprenören i något led) för vilken dokumentation om adekvat utbildning för arbete med ställningar inte kan uppvisas ska Entreprenören betala vite om 25 000 kronor."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="scaffoldingFine"
              nameValue="scaffoldingNumber"
            />
            <Rules
              title="Definition viten arbete med lyftanordningar"
              rule="För varje person (oavsett om arbetsgivare är Entreprenören eller underentreprenören i något led) för vilken dokumentation om adekvat utbildning för arbete med lyftanordningar inte kan uppvisas eller om arbetstagare saknar tillstånd ska Entreprenören betala vite om 35 000 kronor."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="liftingFine"
              nameValue="liftingNumber"
            />
            <Rules
              title="Definition viten arbete med brandfarlig vara"
              rule="För varje person (oavsett om arbetsgivare är Entreprenören eller underentreprenören i något led) som inte kan uppvisa adekvat utbildning/certifiering för arbete med brandfarlig vara (heta arbeten) ska Entreprenören betala vite om 20 000 kronor."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="fireSafetyFine"
              nameValue="fireSafetyNumber"
            />
            <Rules
              title="Definition viten saknar SCT"
              rule="För varje person (oavsett om arbetsgivaren är Entreprenören eller underentreprenören i något led) för vilken dokumentation om adekvat utbildning inom arbetsmiljö och säkerhet (Safe Construction Training) inte kan uppvisas ska Entreprenören betala vite om 25 000 kronor."
            />
            <Fines
              inputLabel="Antal avvikelser"
              klausValue={klausValue}
              nameFine="sctFine"
              nameValue="sctNumber"
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="10">
          <AccordionHeader title="Personlig säkerhetsutrustning" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="safetyEquipmentResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('safetyEquipmentActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="safetyEquipmentActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="safetyEquipmentActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules rule="Personlig skyddsutrustning ska användas i enlighet med Arbetsmiljöverkets föreskrifter om användning av personlig skyddsutrustning (AFS 2001:3) och den ska användas om den är ändamålsenlig i förhållande till risker, anpassad till förhållandena på arbetsplatsen och aktuella arbetsställningar samt arbetarens hälsotillstånd." />
          </S.BodyInner>
        </S.Body>

        <S.Body value="1">
          <AccordionHeader title="Skalskydd/inhägnad" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="shellProtectionResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('shellProtectionActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="shellProtectionActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="shellProtectionActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            {shellProtectionFile && shellProtectionFile.length > 0 && (
              <Photos data={shellProtectionFile} />
            )}

            <Rules
              rule="I enlighet med Arbetsmiljöverkets föreskrifter, AFS 1999:3, ska
                en plats eller ett område där byggnads- eller anläggningsarbete
                utförs avgränsas på väl synligt och lätt identifierbart sätt (23
                §). Anledning till kravet på avgränsning är att obehöriga inte
                skall komma in på plats eller område där byggnads- eller
                anläggningsarbete utförs och där de kan utgöra en risk för
                olyckor eller att stjäla."
            />

            <Fines
              inputLabel="Antal veckor med avvikelse"
              klausValue={klausValue}
              nameFine="shellProtectionFine"
              nameValue="shellProtectionNumber"
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="9">
          <AccordionHeader title="Tecken på onykterhet och drogpåverkan" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="intoxicationResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('intoxicationActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="intoxicationActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="intoxicationActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules
              rule="Under kontrollen fanns ingen antydan på att någon på arbetsplatsen skulle varit påverkad av alkohol eller droger.
              Regelverk: I Arbetsmiljölagen uppges att arbetsgivaren ska klargöra vilka interna regler och rutiner som gäller om arbetstagare uppträder påverkad av alkohol eller andra berusningsmedel i arbetet. Den anställde har rätt att veta vad som gäller på jobbet när det gäller alkohol och droger.
              För att klargöra vad som gäller bör arbetsgivaren upprätta en alkohol- och drogpolicy och en handlingsplan. Policyn ska innehålla tydliga ställningstaganden för hur arbetsplatsen ser på alkohol och droger, till exempel om alkohol får serveras på personalfester och vad som händer om någon kommer påverkad till jobbet. Handlingsplanen ska fungera som en vägledning och tala om hur man ska uppnå det som står i policyn.
              Kontroller ska alltid ske i avskildhet, så att inte någon kränks (11 § Ordningsregler. Punkt 1.6 i Byggavtalet). Provtagningen ska också utföras av personal som är kunnig och vet vad som är rätt när det gäller provtagning och utrustning.
              För att utföra ett drogtest måste arbetsgivaren ha ett skäl. Det vanligaste är säkerheten på arbetsplatsen.
              Det finns inte någon lag som reglerar arbetsgivares möjligheter att testa sin anställda, i stället används kollektiv- eller anställningsavtal. Enligt 11 § MBL ska arbetsgivare förhandla om alkoholkontroller med facket."
            />
          </S.BodyInner>
        </S.Body>

        <S.Body value="11">
          <AccordionHeader title="Övrig kontrollverksamhet" />

          <S.BodyInner>
            <Textarea
              label="Resultat"
              name="otherResult"
              placeholder="Beskriv resultatet..."
            />

            <RadioInput
              state={watch('otherActionsNeeded')}
              control={control}
              question="Åtgärder behövs"
              options={[
                { value: 'no', label: 'Nej' },
                { value: 'yes', label: 'Ja' },
              ]}
              name="otherActionsNeeded"
            />

            <Textarea
              label="Åtgärder"
              name="otherActions"
              placeholder="Beskriv åtgärder som gjorts..."
            />

            <Rules rule="I Diskrimineringslagen framgår att Diskriminering är förbjudet inom arbetslivet. Därför har arbetsgivare ett ansvar för att arbetstagare inte blir utsatta för diskriminering. Arbetsmiljöverkets föreskrifter AFS 2020:1 uppger att arbetsplatser ska ha separata omklädningsrum för män och kvinnor, om de ska användas samtidigt enligt föreskrifter för arbetsplatsens utformning. Likaså ska det finnas skilda duschar för män och kvinnor, i de fall duscharna behöver användas samtidigt." />
          </S.BodyInner>
        </S.Body>

        <S.Footer>
          <S.Button $outlined onClick={navigateToSummary}>
            Föregående
          </S.Button>

          <S.Button onClick={navigateToDeviation}>Nästa</S.Button>
        </S.Footer>
      </S.ResultInner>

      {isGenerateFeatureEnabled &&
        reportTextTemplates.data &&
        statistics.data && (
          <GenerateReportTextDialog
            open={isGenerateDialogOpen}
            onClose={() => setIsGenerateDialogOpen(false)}
            control={data}
            statistics={statistics.data}
            templates={reportTextTemplates.data}
            currentValues={getValues()}
            onApply={applyGeneratedReportText}
          />
        )}
    </S.ResultContainer>
  );
};
